import { Tooltip } from "antd";
import classNames from "classnames";
import React, { ReactNode, RefObject } from "react";
import "./InputText.scss";
import type { TFunction } from "i18next";
import { BORDER_TYPE } from "@Configs/enum";
import CircleExclamation from "assets/icons/CircleExclamation";
import { Close } from "assets/icons";

export interface InputAction {
  name?: ReactNode;
  action?: never;
}

export interface InputTextProps {
  /**Label for current field*/
  label?: string;
  /**Control the style type of component: MATERIAL, BORDERED, FLOAT_LABEL */
  type?: BORDER_TYPE;
  /**Option to set password field */
  typeInput?: "text" | "password";
  /**Show symbol * as required field*/
  isRequired?: boolean;
  /**Prefix for filter value*/
  prefix?: string | JSX.Element;
  /**Suffix for filter value*/
  suffix?: string | JSX.Element;
  /**User-filled value*/
  value?: string;
  /**Not allow to handle change value*/
  disabled?: boolean;
  /**Read only field*/
  readOnly?: boolean;
  /**Placeholder of the component*/
  placeHolder?: string;
  /**Use to custom style the component*/
  className?: string;
  /**Boolean to show the lenght of value user-filled*/
  showCount?: boolean;
  /**Set maximum length of value*/
  maxLength?: number;
  /**Control the size of the component*/
  isSmall?: boolean;
  /**Provide a custom action (onClick) to the component*/
  action?: InputAction;
  /**Handle the change value of the field*/
  onChange?: (T: string | null) => void;
  /**Handle onEnter action*/
  onEnter?: (T: string | null) => void;
  /**Handle onBlur action*/
  onBlur?: (T: string | null) => void;
  /**Handle onKeyDown action*/
  onKeyDown?: (event: unknown) => void;
  /**Handle onKeyDown action*/
  onFocus?: () => void;
  /**Handle onKeyUp action*/
  onKeyUp?: (event: unknown) => void;
  /** Custom background color for component: "white" || "gray" */
  bgColor?: "white" | "gray";
  autoComplete?: boolean;
  nameAttr?: string;
  /** */
  autoFocusInput?: boolean;
  /**Regex to check validate input text */
  regexInput?: RegExp;
  /**Description for error if value match regexInput*/
  regexErrorDescription?: string;
  /**pass translate to explain the description of errors */
  translate?: TFunction;
  /** Allow to remove input content with clear icon */
  allowClear?: boolean;
  /**Check byte count */
  isByteCheck?: boolean;
  isShowTooltip?: boolean;
  /**Pass true to only show error in tooltip */
  isTableCell?: boolean;
}

const InputText = React.forwardRef(
  (props: InputTextProps, ref: React.LegacyRef<HTMLDivElement>) => {
    const {
      action,
      label,
      isRequired = false,
      type = BORDER_TYPE.BORDERED,
      prefix,
      suffix,
      showCount,
      maxLength = 0,
      value,
      disabled = false,
      readOnly = false,
      placeHolder,
      className,
      isSmall = true,
      bgColor = "white",
      typeInput = "text",
      onChange,
      onEnter,
      onBlur,
      onKeyDown,
      onFocus,
      onKeyUp,
      nameAttr,
      autoFocusInput,
      regexInput,
      translate,
      regexErrorDescription,
      allowClear = true,
      isByteCheck = false,
      isTableCell = false,
      isShowTooltip = true,
    } = props;

    const [internalValue, setInternalValue] = React.useState<string>("");

    const [messageValidation, setMessageValidation] =
      React.useState<string>(null);

    const isEdited = React.useRef<boolean>(false);

    const inputRef: RefObject<HTMLInputElement> =
      React.useRef<HTMLInputElement>(null);

    const divRef: RefObject<HTMLDivElement> =
      React.useRef<HTMLDivElement>(null);

    const handleValidateValue = React.useCallback(
      (value: string) => {
        if (
          maxLength &&
          ((isByteCheck && new Blob([value]).size > maxLength) ||
            (!isByteCheck && value?.length > maxLength)) &&
          translate
        ) {
          setMessageValidation(
            `${translate("CM.input_length_validation", { maxLength })}`
          );
        } else if (isRequired && !value?.length && translate) {
          setMessageValidation(`${translate("CM.input_require_validation")}`);
        } else if (regexInput && !regexInput.test(value) && translate) {
          if (regexErrorDescription) {
            setMessageValidation(`${regexErrorDescription}`);
          } else
            setMessageValidation(`${translate("CM.input_regex_validation")}`);
        } else {
          setMessageValidation(null);
        }
      },
      [
        isByteCheck,
        isRequired,
        maxLength,
        regexErrorDescription,
        regexInput,
        translate,
      ]
    );

    const handleChange = React.useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        setInternalValue(event.target.value);
        if (typeof onChange === "function") {
          onChange(event.target.value);
        }
      },
      [onChange]
    );

    const handleClearInput = React.useCallback(() => {
      if (isRequired && translate) {
        setMessageValidation(`${translate("CM.input_require_validation")}`);
      } else {
        setMessageValidation(null);
      }
      setInternalValue("");
      if (inputRef && inputRef.current) {
        inputRef.current.focus();
      }
      if (typeof onChange === "function") {
        onChange(undefined);
        return;
      }
    }, [isRequired, onChange, translate]);

    const handleKeyPress = React.useCallback(
      (event: React.KeyboardEvent<HTMLInputElement> | undefined) => {
        if (event.key === "Enter") {
          if (typeof onEnter === "function") {
            onEnter(event.currentTarget.value);
          }
        }
      },
      [onEnter]
    );

    const handleKeyDown = React.useCallback(
      (event: React.KeyboardEvent<HTMLInputElement> | undefined) => {
        if (typeof onKeyDown === "function") {
          onKeyDown(event);
        }
      },
      [onKeyDown]
    );

    const handleBlur = React.useCallback(
      (event: React.FocusEvent<HTMLInputElement>) => {
        if (typeof onBlur === "function") {
          onBlur(event.currentTarget.value);
        }
      },
      [onBlur]
    );

    React.useEffect(() => {
      if (value) {
        setInternalValue(value);
        handleValidateValue(value);
      } else {
        setInternalValue("");
        if (isEdited.current === true) {
          handleValidateValue("");
        }
      }
      isEdited.current = true;
    }, [handleValidateValue, value]);

    React.useEffect(() => {
      if (autoFocusInput) {
        inputRef.current.focus();
      }
    }, [autoFocusInput]);

    return (
      <div className={classNames("input-text__wrapper", className)}>
        {(label || showCount || action?.name) && (
          <div className="input-text__label m-b--3xs p-l--3xs">
            {type !== BORDER_TYPE.FLOAT_LABEL && label && (
              <label
                className={classNames("component__title", {
                  "component__title--disabled": disabled,
                })}
              >
                {label}
                {isRequired && <span className="text-danger">&nbsp;*</span>}
              </label>
            )}
            <div className="label__right">
              {showCount && maxLength > 0 && (
                <span className="input-text__count p-l--xs body-text--xs">
                  {internalValue.length}/{maxLength}
                </span>
              )}
              {action && (
                <span
                  className="m-l--3xs body-text--md color-link"
                  style={{ cursor: "pointer" }}
                  onClick={action?.action}
                >
                  {action?.name}
                </span>
              )}
            </div>
          </div>
        )}
        <div
          className={classNames("component__input input-text__container", {
            "input-text__container--sm": isSmall,
            "input-text__container--white": bgColor === "white",
            "p--2xs": isSmall,
            "p--xs": !isSmall,
            "input-text--material": type === BORDER_TYPE.MATERIAL,
            "input-text--bordered": type === BORDER_TYPE.BORDERED,
            "input-text--disabled ": disabled,
            "input-text--float": type === BORDER_TYPE.FLOAT_LABEL,
            "input-text--readOnly": readOnly,
          })}
          ref={ref}
          onClick={() => {
            inputRef.current.focus();
          }}
        >
          {prefix && (
            <>
              {typeof prefix === "string" ? (
                <span className="p-r--2xs input-text__string">{prefix}</span>
              ) : (
                <div className="m-r--xs input-text__icon">{prefix}</div>
              )}
            </>
          )}
          <Tooltip
            color={isTableCell && messageValidation ? "red" : "black"}
            overlayClassName="input-error__tooltip"
            title={isTableCell ?
              (messageValidation ?
                messageValidation :
                (isShowTooltip ?
                  internalValue :
                  undefined)) :
              (isShowTooltip ?
                internalValue :
                undefined)}
            placement="topLeft"
          >
            <div style={{ flexGrow: 1 }}>
              <input
                type={typeInput}
                value={internalValue}
                onChange={handleChange}
                onKeyPress={handleKeyPress}
                onKeyDown={handleKeyDown}
                onBlur={handleBlur}
                onFocus={onFocus}
                onKeyUp={onKeyUp}
                placeholder={
                  type === BORDER_TYPE.FLOAT_LABEL && label ? " " : placeHolder
                }
                ref={inputRef}
                disabled={disabled}
                className={classNames("component__input", {
                  "disabled-field": disabled,
                })}
                autoComplete="new-password"
                name={nameAttr}
                readOnly={readOnly}
              />
            </div>
          </Tooltip>
          {type === BORDER_TYPE.FLOAT_LABEL && label && (
            <label
              className={classNames("component__title", {
                "component__title--normal": !prefix,
                "component__title--prefix": prefix,
                "component__title--sm": isSmall,
              })}
            >
              {label}
              {isRequired && <span className="text-danger">&nbsp;*</span>}
            </label>
          )}
          {allowClear && internalValue && !disabled && !readOnly && (
            <div className={classNames("input-icon__clear", "m-l--2xs")}>
              <Close onClick={handleClearInput} />
            </div>
          )}
          {suffix && (
            <>
              {typeof suffix === "string" ? (
                <span className="m-l--2xs input-text__string">{suffix}</span>
              ) : (
                <div className="m-l--2xs input-text__icon">{suffix}</div>
              )}
            </>
          )}
        </div>
        {messageValidation ? (
          <div
            className={classNames("message-validation", {
              "is-table-cell": isTableCell,
            })}
          >
            <CircleExclamation />
            <span className="m-l--3xs">{messageValidation}</span>
          </div>
        ) : (
          <></>
        )}

        <div className="input-text__hidden" ref={divRef}>
          {internalValue}
        </div>
      </div>
    );
  }
);
InputText.displayName = "InputText";

export default InputText;
